/** The app bar (2026-10-02): mail search on every page, as in Outlook.
 *  ⌘K (Ctrl+K) or / from anywhere puts the cursor in it; running a search
 *  opens it in Mail's Search section (/search?q=…), where the results can
 *  be refined, saved and acted on.
 *
 *  A band at the top of the page, below the window's own title bar, in a
 *  browser and in Waki Mail from 0.2.2 (2026-10-03: in the title bar it was
 *  cramped). Waki Mail 0.2.0–0.2.1 hid the native title bar and put this
 *  bar beside the window buttons, announcing itself as
 *  `window.__wakiMailChrome`; that still works until those builds are gone.
 *
 *  At the left, Home opens the dashboard the app starts on (Waki Mail:
 *  Command-Shift-H), Inbox goes back to Outlook Folders › Inbox from
 *  anywhere, and View, the mail views' date range, ends where the nav
 *  column ends (2026-10-03: at the far right it went unseen). Search sits
 *  centred over the columns to the right of the nav. */
import { useMailScope } from "../lib/mailScope";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Home, Inbox, PencilLine } from "lucide-react";
import { useHistoryNav } from "../lib/historyNav";
import { useLocation, useNavigate } from "react-router-dom";
import { bridgeFetch } from "../lib/bridgeFetch";
import { SearchBox } from "../modules/mail/components/SearchBox";
import { useSavedSearches } from "../modules/mail/savedSearches";
import { rememberSearch } from "../modules/mail/searchSuggest";
import { RangePicker } from "../modules/mail/components/RangePicker";
import { onMailRangeChange, readMailRange, writeMailRange } from "../modules/mail/dateRanges";
import { useNavEdge } from "../lib/navEdge";

type MacChrome = { titleBar?: "hidden"; inset?: number; height?: number };

function macChrome(): MacChrome | null {
  const c = (window as unknown as { __wakiMailChrome?: MacChrome }).__wakiMailChrome;
  return c && c.titleBar === "hidden" ? c : null;
}

type TreeNode = { name: string; children?: TreeNode[] };

/** Back to Outlook Folders › Inbox, from anywhere: Mail picks this up
 *  (or lands there itself when it opens). */
export function goHomeEvent(): void {
  window.dispatchEvent(new Event("wov:go-home"));
}

/** Where the first search of a run started (any page), to go back to
 *  when the search is closed. Mail also remembers its own view exactly. */
let preSearchPath: string | null = null;

/** Close the search on screen: back to the view it started from. */
export function exitSearch(): void {
  window.dispatchEvent(new Event("wov:exit-search"));
}

/** Put the cursor in the title bar's search, from anywhere. */
export function focusTitleSearch(): void {
  window.dispatchEvent(new Event("wov:focus-search"));
}

export function TitleBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  // Back and Forward through the app (⌘[ and ⌘], or Alt+← and Alt+→).
  const hist = useHistoryNav();
  const histRef = useRef(hist);
  histRef.current = hist;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      const back = ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === "[") || (e.altKey && e.key === "ArrowLeft" && !typing);
      const fwd = ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === "]") || (e.altKey && e.key === "ArrowRight" && !typing);
      if (back && histRef.current.canBack) {
        e.preventDefault();
        histRef.current.back();
      } else if (fwd && histRef.current.canForward) {
        e.preventDefault();
        histRef.current.forward();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const inSearch = location.pathname === "/search" || location.pathname === "/ask";
  // The folder on screen, for "Search <folder> for …" (Mail and its search only).
  const mailScope = useMailScope();
  const onMailPages = location.pathname.startsWith("/mail") || location.pathname === "/search";
  const urlQ = inSearch ? new URLSearchParams(location.search).get("q") ?? "" : "";
  const [value, setValue] = useState(urlQ);
  // The box shows the search on screen; leaving it clears the box.
  useEffect(() => setValue(urlQ), [urlQ]);
  const { searches } = useSavedSearches();
  // The Mac app reports its title bar's real height once its window is up.
  const [, setChromeTick] = useState(0);
  useEffect(() => {
    const on = () => setChromeTick((t) => t + 1);
    window.addEventListener("wov:chrome", on);
    return () => window.removeEventListener("wov:chrome", on);
  }, []);
  const chrome = macChrome();

  // Folders and categories for the suggestions, read once the box is used.
  const [folders, setFolders] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const loadedRef = useRef(false);
  const loadContext = useCallback(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void bridgeFetch<{ ok: boolean; data?: { sections: Array<{ children: TreeNode[] }> } }>("/api/bridge/v1/folders/tree", { priority: "user" }).then((r) => {
      if (!r.ok || !r.data.data) return;
      const out: string[] = [];
      const walk = (nodes: TreeNode[] | undefined, parent: string) => {
        for (const n of nodes ?? []) {
          const path = parent ? `${parent}/${n.name}` : n.name;
          out.push(path);
          walk(n.children, path);
        }
      };
      for (const s of r.data.data.sections) walk(s.children, "");
      setFolders(out);
    });
    void bridgeFetch<{ ok: boolean; data?: { categories: Array<{ name: string }> } }>("/api/bridge/v1/categories", { priority: "user" }).then((r) => {
      if (r.ok && r.data.data) setCategories(r.data.data.categories.map((c) => c.name));
    });
  }, []);

  // ⌘K / Ctrl+K anywhere; / when not typing; and asks from the pages.
  useEffect(() => {
    const focus = () => {
      loadContext();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    const onKey = (e: KeyboardEvent) => {
      // An editor used the key (Cmd+K for a link in the message editor).
      if (e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        focus();
      } else if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        focus();
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("wov:focus-search", focus);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("wov:focus-search", focus);
    };
  }, [loadContext]);

  // Closing a search (the box's ×, Esc, Close search): Mail restores the
  // view it was showing; otherwise back to the page the search began on.
  const closeSearch = useCallback(() => {
    if (location.pathname === "/ask") {
      setValue("");
      navigate(preSearchPath ?? "/", { replace: true });
      preSearchPath = null;
      return;
    }
    if (location.pathname !== "/search") return;
    setValue("");
    const ev = new Event("wov:search-cleared", { cancelable: true });
    const handled = !window.dispatchEvent(ev);
    if (!handled) navigate(preSearchPath ?? "/", { replace: true });
    preSearchPath = null;
  }, [location.pathname, navigate]);
  useEffect(() => {
    window.addEventListener("wov:exit-search", closeSearch);
    return () => window.removeEventListener("wov:exit-search", closeSearch);
  }, [closeSearch]);

  // View: the mail views' date range, on the mail pages (not Search, which
  // has its own dates).
  const [range, setRange] = useState(() => readMailRange().value);
  useEffect(() => onMailRangeChange((v) => setRange(v)), []);
  // Home shows it too: its Triage numbers are for the same range.
  const onMail = location.pathname === "/" || location.pathname.startsWith("/mail");
  const onHome = location.pathname === "/";

  const goInbox = useCallback(() => {
    if (!location.pathname.startsWith("/mail")) navigate("/mail");
    window.setTimeout(goHomeEvent, 0);
  }, [location.pathname, navigate]);
  const goDashboard = useCallback(() => {
    if (location.pathname !== "/") navigate("/");
  }, [location.pathname, navigate]);
  // Waki Mail's Home menu item (Command-Shift-H) asks for Home.
  useEffect(() => {
    window.addEventListener("wov:home-request", goDashboard);
    return () => window.removeEventListener("wov:home-request", goDashboard);
  }, [goDashboard]);

  // The Mac app drags the window from the bar, except over its controls.
  const boxRef = useRef<HTMLDivElement>(null);
  const homeRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!chrome) return;
    const post = () => {
      const h = (window as unknown as { webkit?: { messageHandlers?: Record<string, { postMessage: (m: unknown) => void }> } }).webkit?.messageHandlers?.wovChrome;
      const rects = [boxRef.current, homeRef.current, viewRef.current]
        .map((el) => el?.getBoundingClientRect())
        .filter((r): r is DOMRect => !!r)
        .map((r) => ({ x: r.left, y: r.top, w: r.width, h: r.height }));
      if (h) h.postMessage({ noDrag: rects });
    };
    post();
    const ro = new ResizeObserver(post);
    if (boxRef.current) ro.observe(boxRef.current);
    if (homeRef.current) ro.observe(homeRef.current);
    if (viewRef.current) ro.observe(viewRef.current);
    window.addEventListener("resize", post);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", post);
    };
  }, [!!chrome, onMail]); // eslint-disable-line react-hooks/exhaustive-deps

  // The left group reaches the nav column's edge, so View ends where it does.
  const navEdge = useNavEdge();
  const leftRef = useRef<HTMLDivElement>(null);
  const [leftStart, setLeftStart] = useState<number | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      if (leftRef.current) setLeftStart(Math.round(leftRef.current.getBoundingClientRect().left));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [chrome?.inset]);
  const leftWidth = navEdge !== null && leftStart !== null ? Math.max(0, navEdge - leftStart) : undefined;
  // "View" is dropped (the picker's tooltip says what it is) only when that
  // is what lets it end at the nav column's edge.
  const [showViewLabel, setShowViewLabel] = useState(true);
  const viewLabelW = useRef(0);
  useLayoutEffect(() => {
    const home = homeRef.current;
    const view = viewRef.current;
    if (!home || !view || leftWidth === undefined) {
      setShowViewLabel(true);
      return;
    }
    const label = view.querySelector<HTMLElement>("[data-range-label]");
    if (label) viewLabelW.current = label.offsetWidth + 4; // its gap
    const withLabel = view.offsetWidth + (label ? 0 : viewLabelW.current);
    const without = withLabel - viewLabelW.current;
    const room = leftWidth - home.offsetWidth - 8; // the group's gap
    setShowViewLabel(withLabel <= room || without > room);
  }, [leftWidth, range, onMail, showViewLabel]);

  const height = chrome?.height ?? 44;
  return (
    <div
      className="wov-titlebar relative flex flex-shrink-0 items-center gap-3 px-2.5"
      style={{ height, paddingLeft: chrome ? Math.max(12, chrome.inset ?? 80) : undefined }}
      onDoubleClick={(e) => {
        // The Mac app zooms the window natively; nothing to do in a browser.
        if (e.target === e.currentTarget) e.preventDefault();
      }}
    >
      <div ref={leftRef} className="flex flex-shrink-0 items-center gap-2" style={{ minWidth: leftWidth }}>
        <div ref={homeRef} className="inline-flex items-center gap-0.5">
          {(["back", "forward"] as const).map((d) => {
            const can = d === "back" ? hist.canBack : hist.canForward;
            const Icon = d === "back" ? ChevronLeft : ChevronRight;
            return (
              <button
                key={d}
                type="button"
                disabled={!can}
                onClick={d === "back" ? hist.back : hist.forward}
                title={d === "back" ? (chrome ? "Back (⌘[)" : "Back (Alt+←)") : chrome ? "Forward (⌘])" : "Forward (Alt+→)"}
                aria-label={d === "back" ? "Back" : "Forward"}
                className={
                  "inline-flex items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-200/70 hover:text-slate-900 active:scale-95 disabled:pointer-events-none disabled:opacity-30 dark:text-slate-300 dark:hover:bg-slate-700/60 dark:hover:text-white " +
                  (chrome && height < 36 ? "h-[24px] w-[24px]" : "h-8 w-8")
                }
              >
                <Icon className="h-[18px] w-[18px]" />
              </button>
            );
          })}
          <span aria-hidden className="mx-1 h-4 w-px bg-slate-300/70 dark:bg-slate-600/70" />
          <button
            type="button"
            onClick={goDashboard}
            title={chrome ? "Home: what needs you (⌘⇧H)" : "Home: what needs you"}
            aria-label="Home dashboard"
            aria-current={onHome ? "page" : undefined}
            className={
              "inline-flex items-center gap-1.5 rounded-lg px-2 font-semibold transition active:scale-95 " +
              (onHome
                ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200 dark:bg-slate-700/70 dark:text-white dark:ring-slate-600 "
                : "text-slate-600 hover:bg-slate-200/70 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-700/60 dark:hover:text-white ") +
              (chrome && height < 36 ? "h-[24px] text-[12px]" : "h-8 text-[13px]")
            }
          >
            <Home className="h-4 w-4 text-amber-500 dark:text-amber-400" />
            <span className="hidden sm:inline">Home</span>
          </button>
          <button
            type="button"
            onClick={goInbox}
            title="Inbox: Outlook Folders › Inbox"
            aria-label="Inbox: Outlook Folders, Inbox"
            className={
              "inline-flex items-center gap-1.5 rounded-lg px-2 font-semibold text-slate-600 transition hover:bg-slate-200/70 hover:text-slate-900 active:scale-95 dark:text-slate-300 dark:hover:bg-slate-700/60 dark:hover:text-white " +
              (chrome && height < 36 ? "h-[24px] text-[12px]" : "h-8 text-[13px]")
            }
          >
            <Inbox className="h-4 w-4 text-sky-600 dark:text-sky-400" />
            <span className="hidden sm:inline">Inbox</span>
          </button>
          {/* New mail, from any page (2026-10-06): in Mail it opens the
              compose window; elsewhere it opens Mail with one. The title is
              exactly "New mail": the Mac app's File > New Message (⌘N)
              clicks the button with that title. */}
          <button
            type="button"
            onClick={() => {
              if (location.pathname.startsWith("/mail") || location.pathname === "/search") window.dispatchEvent(new Event("wov:compose"));
              else navigate("/mail?compose=1");
            }}
            title="New mail"
            aria-keyshortcuts="c"
            className={
              "ml-1 inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-2.5 font-semibold text-white shadow-sm transition hover:bg-sky-500 active:scale-95 " +
              (chrome && height < 36 ? "h-[24px] text-[12px]" : "h-8 text-[13px]")
            }
          >
            <PencilLine className="h-4 w-4" />
            <span className="hidden sm:inline">New mail</span>
          </button>
        </div>
        {onMail && (
          <div ref={viewRef} className="ml-auto">
            <RangePicker value={range} onChange={writeMailRange} label={showViewLabel ? "View" : undefined} compact={!!chrome && height < 36} />
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-1 justify-center">
        <div ref={boxRef} className="w-full max-w-[620px]" onFocusCapture={loadContext}>
          <SearchBox
            inputRef={inputRef}
            value={value}
            onChange={setValue}
            onSubmit={(q, said) => {
              rememberSearch(q);
              if (!inSearch) preSearchPath = location.pathname + location.search;
              navigate(`/search?q=${encodeURIComponent(q)}${said ? `&said=${encodeURIComponent(said)}` : ""}`);
            }}
            onAsk={(q) => {
              if (!inSearch) preSearchPath = location.pathname + location.search;
              navigate(`/ask?q=${encodeURIComponent(q)}`);
            }}
            onClear={closeSearch}
            scope={onMailPages ? mailScope : null}
            refreshing={false}
            folders={folders}
            categories={categories}
            saved={searches}
            size={chrome && height < 44 ? "sm" : "md"}
          />
        </div>
      </div>
    </div>
  );
}
